import { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import Modal from '../../components/Modal.jsx';
import { api } from '../../api/client.js';
import { problem } from '../bookings/util.js';

// A PDF from a message, shown inside the app page by page (pdf.js), so it
// opens the same in a browser and in the Android app, which can't show PDFs
// itself. pdf.js only loads when a PDF is opened. Its worker is bundled as a
// plain .js file (the server doesn't serve .mjs as JavaScript) and started
// once, then reused.
let worker = null;
const pdfWorker = () => {
  worker ||= new Worker(new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url), { type: 'module' });
  return worker;
};

export default function PdfViewer({ fileId, name, onClose }) {
  const box = useRef(null);
  const [state, setState] = useState({ status: 'loading', pages: 0, url: null, error: null });

  useEffect(() => {
    let live = true;
    let url = null;
    let doc = null;
    (async () => {
      try {
        const [blob, pdfjs] = await Promise.all([
          api(`/messages/files/${fileId}`, { as: 'blob' }),
          import('pdfjs-dist'),
        ]);
        if (!live) return;
        url = URL.createObjectURL(blob);
        pdfjs.GlobalWorkerOptions.workerPort ||= pdfWorker();
        doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) }).promise;
        if (!live) return;
        setState({ status: 'ready', pages: doc.numPages, url, error: null });
        const width = Math.min(box.current?.clientWidth || 800, 1000);
        const ratio = window.devicePixelRatio || 1;
        for (let i = 1; i <= doc.numPages && live; i += 1) {
          const page = await doc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const view = page.getViewport({ scale: (width / base.width) * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = view.width;
          canvas.height = view.height;
          canvas.style.width = `${view.width / ratio}px`;
          canvas.className = 'pdf-page';
          canvas.setAttribute('aria-label', `Page ${i}`);
          await page.render({ canvas, canvasContext: canvas.getContext('2d'), viewport: view }).promise;
          if (live) box.current?.appendChild(canvas);
        }
      } catch (e) {
        if (live) setState({ status: 'error', pages: 0, url, error: e?.status ? problem(e) : 'The document couldn’t be opened. It shows when you have signal.' });
      }
    })();
    return () => {
      live = false;
      doc?.destroy?.();
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);

  return (
    <Modal
      open
      wide
      title={name || 'Document'}
      onClose={onClose}
      footer={state.url && (
        <a className="btn with-icon" href={state.url} download={name || 'Document.pdf'}><Download size={16} aria-hidden="true" /> Download</a>
      )}
    >
      {state.status === 'loading' && <p className="muted">Opening…</p>}
      {state.status === 'error' && <p className="notice-line notice-warn">{state.error}</p>}
      {state.status === 'ready' && <p className="small muted">{state.pages} {state.pages === 1 ? 'page' : 'pages'}</p>}
      <div className="pdf-pages" ref={box} />
    </Modal>
  );
}
